/**
 * LayerManager - 레이어 관리 클래스
 */

import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Style, Fill, Stroke, Circle as CircleStyle } from 'ol/style';
import { createEmpty, extendCoordinate } from 'ol/extent';
import { fromLonLat } from 'ol/proj';
import { eventBus, Events } from '../utils/EventBus.js';
import { mapManager } from './MapManager.js';
import { strokeWidthOf, makeStroke } from '../utils/strokeStyle.js';
import { cloneChoroplethConfig } from '../tools/choroplethConfigSerial.js';

/**
 * 레이어별 스타일 메타데이터 필드.
 *
 * 복제·복원 경로가 공유한다. 여기 빠뜨린 필드는 프로젝트를 다시 열면 조용히
 * 기본값으로 돌아간다. 새 스타일 필드를 추가하면 반드시 여기에도 넣을 것.
 * (직렬화 쪽은 배열이 아니라 개별 속성 나열이므로 StateManager.saveLayer와
 *  ProjectManager의 저장 코드도 함께 고쳐야 한다.)
 */
export const STYLE_FIELDS = [
  'strokeColor', 'fillColor', 'fillOpacity', 'strokeOpacity',
  'strokeWidth', 'strokeDash', 'pointRadius', 'strokeSyncToFill'
];

/**
 * layerInfo에서 스타일 필드만 뽑아 직렬화용 객체로 만든다.
 *
 * 저장 경로(StateManager.saveLayer, ProjectManager)가 공유한다. 복원 경로는
 * STYLE_FIELDS를 직접 순회하므로, 이 헬퍼까지 쓰면 필드 추가가 배열 한 줄로 끝난다.
 * 값이 undefined면 JSON.stringify가 알아서 생략한다 — 기존 동작과 같다.
 */
export function pickStyleFields(layerInfo) {
  const out = {};
  for (const key of STYLE_FIELDS) out[key] = layerInfo[key];
  return out;
}

/** 새 레이어에 자동으로 돌아가며 주는 색 (서로 잘 구분되는 색만) */
const AUTO_COLOR_SEQUENCE = [
  '#3b82f6', '#ef4444', '#10b981', '#f59e0b',
  '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'
];

/** 스타일 편집에서 고르는 기본 8색 */
const SWATCH_PALETTE = [
  { color: '#ffffff', name: '흰색' },
  { color: '#808080', name: '회색' },
  { color: '#000000', name: '검은색' },
  { color: '#e53935', name: '빨강' },
  { color: '#fb8c00', name: '주황' },
  { color: '#fdd835', name: '노랑' },
  { color: '#43a047', name: '초록' },
  { color: '#1e88e5', name: '파랑' }
];

/**
 * 새 폴리곤 레이어에 순서대로 주는 색 — 스타일 편집 팔레트와 같은 순서(흰색부터).
 * 첫 레이어를 흰색으로 두는 건 밑그림(행정경계) 위에 색을 쌓아가는 수업 흐름 때문이다.
 * 포인트·선은 흰색·검은색이 잘 안 보여 기존 AUTO_COLOR_SEQUENCE를 그대로 쓴다.
 */
const POLYGON_COLOR_SEQUENCE = SWATCH_PALETTE.map(s => s.color);

/** 면 색 위에서 보이는 기본 선 색 — 검은 면에 검은 선을 그으면 테두리가 사라진다 */
function defaultStrokeFor(fillColor) {
  const hex = String(fillColor || '').trim().toLowerCase();
  const isBlack = hex === '#000000' || hex === '#000';
  return isBlack ? '#ffffff' : '#000000';
}

/** 면이 있는(채우기가 보이는) 지오메트리인가 */
function isPolygonType(geometryType) {
  return geometryType === 'Polygon' || geometryType === 'MultiPolygon';
}

const STROKE_DASH_OPTIONS = {
  solid: null,
  dashed: [10, 10],
  dotted: [2, 6],
  "dash-dot": [10, 5, 2, 5]
};

class LayerManager {
  constructor() {
    this.layers = new Map();
    this.layerOrder = [];
    this.selectedLayerId = null;
    this.selectedLayerIds = new Set(); // 다중 선택 지원
    this.colorIndex = 0;
    this.polygonColorIndex = 0;
  }

  getNextColor() {
    const color = AUTO_COLOR_SEQUENCE[this.colorIndex % AUTO_COLOR_SEQUENCE.length];
    this.colorIndex++;
    return color;
  }

  /** 폴리곤은 팔레트 순서(흰색 → 회색 → 검은색 → 유채색)대로 배정한다 */
  getNextPolygonColor() {
    const color = POLYGON_COLOR_SEQUENCE[this.polygonColorIndex % POLYGON_COLOR_SEQUENCE.length];
    this.polygonColorIndex++;
    return color;
  }

  /** 지오메트리에 맞는 자동 색 (폴리곤만 팔레트 순서) */
  pickAutoColor(geometryType) {
    return isPolygonType(geometryType) ? this.getNextPolygonColor() : this.getNextColor();
  }

  /**
   * 분류 레이어인가 (단계구분도·카토그램).
   *
   * 이런 레이어는 채우기 색을 분류 설정이 소유한다. 사용자는 투명도와 테두리만 바꾼다.
   * 카토그램은 type이 'cartogram'이 아니라 'vector'라(CartogramTool.js:205)
   * type만 봐서는 못 잡는다. 설정 객체의 존재로 식별한다.
   */
  isClassified(layerInfo) {
    if (!layerInfo) return false;
    return layerInfo.type === 'choropleth' || !!layerInfo._cartogramConfig;
  }

  getLineDash(strokeDash) {
    return STROKE_DASH_OPTIONS[strokeDash] || null;
  }

  hexToRgba(hex, alpha) {
    if (alpha === undefined) alpha = 1;
    // 유효하지 않은 hex 값 처리
    if (!hex || typeof hex !== 'string') {
      return "rgba(128, 128, 128, " + alpha + ")";
    }
    // 이미 rgba 형식인 경우 그대로 반환
    if (hex.startsWith('rgba') || hex.startsWith('rgb')) {
      return hex;
    }
    // # 없으면 추가
    if (!hex.startsWith('#')) {
      hex = '#' + hex;
    }
    const r = parseInt(hex.slice(1, 3), 16) || 0;
    const g = parseInt(hex.slice(3, 5), 16) || 0;
    const b = parseInt(hex.slice(5, 7), 16) || 0;
    return "rgba(" + r + ", " + g + ", " + b + ", " + alpha + ")";
  }

  createStyle(color, geometryType) {
    if (!geometryType) geometryType = "polygon";
    // 폴리곤 면은 불투명하게 깔고, 테두리는 면 색에 맞춰 보이는 색으로 (addLayer 기본값과 같은 규약)
    const rgbaColor = this.hexToRgba(color, 1);

    if (geometryType === "Point" || geometryType === "MultiPoint") {
      return new Style({
        image: new CircleStyle({
          radius: 6,
          fill: new Fill({ color: color }),
          stroke: new Stroke({ color: "#ffffff", width: 2 })
        })
      });
    } else if (geometryType === "LineString" || geometryType === "MultiLineString") {
      return new Style({
        stroke: new Stroke({ color: color, width: 3 })
      });
    } else {
      return new Style({
        fill: new Fill({ color: rgbaColor }),
        stroke: new Stroke({ color: defaultStrokeFor(color), width: 2 })
      });
    }
  }

  addLayer(options) {
    const name = options.name || "새 레이어";
    const type = options.type || "vector";
    const source = options.source || null;
    const features = options.features || [];
    const visible = options.visible !== undefined ? options.visible : true;
    const color = options.color || null;
    const style = options.style || null;
    const zIndex = options.zIndex !== undefined ? options.zIndex : null;
    const existingLayer = options.olLayer || null; // 기존 레이어 지원

    const layerId = options.id || ("layer-" + Date.now() + "-" + Math.random().toString(36).substr(2, 9));
    // 자동 색은 지오메트리에 따라 다르므로(폴리곤=팔레트 순서) geometryType이 정해진 뒤에 고른다
    let layerColor = color;
    const resolveColor = () => {
      if (!layerColor) layerColor = this.pickAutoColor(geometryType);
      return layerColor;
    };

    let vectorSource = null;
    let olLayer;
    let geometryType = options.geometryType || "Polygon";
    let featureCount = 0;

    // geometryType 결정 함수 (벡터 레이어용)
    const detectGeometryType = (src) => {
      if (options.geometryType) return options.geometryType;
      if (src && typeof src.getFeatures === 'function') {
        const firstFeature = src.getFeatures()[0];
        if (firstFeature && firstFeature.getGeometry()) {
          return firstFeature.getGeometry().getType();
        }
      }
      return "Polygon";
    };

    console.log('addLayer 호출:', { type, hasExistingLayer: !!existingLayer, name });

    // 래스터 레이어 처리
    if (type === 'raster' && existingLayer) {
      console.log('래스터 레이어로 처리');
      olLayer = existingLayer;
      geometryType = options.geometryType || 'Raster';
      featureCount = 0;
    }
    // 기존 레이어가 전달된 경우 (히트맵 등)
    else if (existingLayer) {
      console.log('기존 레이어로 처리 (히트맵 등)');
      olLayer = existingLayer;
      vectorSource = olLayer.getSource && olLayer.getSource();
      if (vectorSource && typeof vectorSource.getFeatures === 'function') {
        geometryType = detectGeometryType(vectorSource);
        featureCount = vectorSource.getFeatures().length;
      }
    } else {
      console.log('새 벡터 레이어 생성');
      if (source) {
        vectorSource = source;
      } else {
        vectorSource = new VectorSource();
        if (features.length > 0) {
          vectorSource.addFeatures(features);
        }
      }

      geometryType = detectGeometryType(vectorSource);
      const layerStyle = style || this.createStyle(resolveColor(), geometryType);

      olLayer = new VectorLayer({
        source: vectorSource,
        style: layerStyle,
        visible: visible
      });

      featureCount = vectorSource.getFeatures().length;
    }

    const newZIndex = zIndex !== null ? zIndex : this.layerOrder.length + 1;
    olLayer.setZIndex(newZIndex);

    resolveColor();
    // 폴리곤: 면은 불투명(100%), 선은 면 색 위에서 보이는 색.
    // 포인트·선은 예전 그대로(색=선색, 면 30%) — 흰 점·검은 점은 배경에 묻힌다.
    const polygonal = isPolygonType(geometryType);
    const pointish = geometryType === "Point" || geometryType === "MultiPoint";
    const linear = geometryType === "LineString" || geometryType === "MultiLineString";

    const layerInfo = {
      id: layerId,
      name: name,
      type: type,
      olLayer: olLayer,
      source: vectorSource,
      visible: visible,
      color: layerColor,
      // 아래 기본값은 createStyle이 처음 그리는 모습과 같아야 한다.
      // 어긋나 있으면 스타일 편집기를 한 번 건드리는 순간(updateLayerStyle) 레이어가
      // 눈에 띄게 달라진다 — 점의 흰 테두리가 색으로 바뀌고 반투명해지는 식으로.
      strokeColor: polygonal ? defaultStrokeFor(layerColor) : (pointish ? "#ffffff" : layerColor),
      fillColor: layerColor,
      geometryType: geometryType,
      // 가져올 때 판정한 원본 좌표계. 피처는 3857로 저장되므로 나중에
      // 좌표계를 다시 지정하려면 어디서 왔는지 알아야 한다. 지금은 기록만 한다.
      sourceCrs: options.sourceCrs || null,
      featureCount: featureCount,
      strokeDash: "solid",
      strokeSyncToFill: true,
      fillOpacity: linear ? 0.3 : 1.0,
      strokeOpacity: 1.0,
      strokeWidth: linear ? 3 : 2,
      pointRadius: 6
    };

    this.layers.set(layerId, layerInfo);
    this.layerOrder.push(layerId);

    const map = mapManager.getMap();
    if (map) {
      map.addLayer(olLayer);
    }

    eventBus.emit(Events.LAYER_ADDED, { layer: layerInfo });
    return layerId;
  }

  removeLayer(layerId) {
    const layerInfo = this.layers.get(layerId);
    if (!layerInfo) return false;

    const map = mapManager.getMap();
    if (map) {
      map.removeLayer(layerInfo.olLayer);
    }

    this.layers.delete(layerId);
    this.layerOrder = this.layerOrder.filter(function(id) { return id !== layerId; });

    if (this.selectedLayerId === layerId) {
      this.selectedLayerId = null;
    }

    // 다중 선택에서도 제거
    this.selectedLayerIds.delete(layerId);

    // 레이어가 하나도 없으면 색 순서를 처음으로 — 새로 시작할 때 첫 폴리곤은 다시 흰색이어야 한다
    if (this.layers.size === 0) {
      this.colorIndex = 0;
      this.polygonColorIndex = 0;
    }

    eventBus.emit(Events.LAYER_REMOVED, { layerId: layerId });
    return true;
  }

  toggleVisibility(layerId) {
    const layerInfo = this.layers.get(layerId);
    if (!layerInfo) return;

    const newVisibility = !layerInfo.visible;
    layerInfo.visible = newVisibility;
    layerInfo.olLayer.setVisible(newVisibility);

    eventBus.emit(Events.LAYER_VISIBILITY_CHANGED, {
      layerId: layerId,
      visible: newVisibility
    });
  }

  selectLayer(layerId) {
    const prevSelectedId = this.selectedLayerId;
    this.selectedLayerId = layerId;

    // 단일 선택 시 다중 선택 초기화
    this.selectedLayerIds.clear();
    if (layerId) {
      this.selectedLayerIds.add(layerId);
    }

    eventBus.emit(Events.LAYER_SELECTED, {
      layerId: layerId,
      prevLayerId: prevSelectedId
    });
  }

  /**
   * Ctrl/Shift 키와 함께 레이어 선택
   * @param {string} layerId - 선택할 레이어 ID
   * @param {boolean} ctrlKey - Ctrl 키 눌림 여부
   * @param {boolean} shiftKey - Shift 키 눌림 여부
   */
  selectLayerWithModifier(layerId, ctrlKey, shiftKey) {
    if (!ctrlKey && !shiftKey) {
      // 일반 클릭: 단일 선택
      this.selectLayer(layerId);
      return;
    }

    if (ctrlKey) {
      // Ctrl+클릭: 토글 선택
      this.toggleLayerSelection(layerId);
    } else if (shiftKey && this.selectedLayerId) {
      // Shift+클릭: 범위 선택
      this.selectRange(this.selectedLayerId, layerId);
    } else {
      // Shift만 눌렸지만 기존 선택이 없으면 단일 선택
      this.selectLayer(layerId);
    }
  }

  /**
   * 레이어 선택 토글 (Ctrl+클릭)
   */
  toggleLayerSelection(layerId) {
    if (this.selectedLayerIds.has(layerId)) {
      this.selectedLayerIds.delete(layerId);
      // 마지막 선택된 레이어가 해제되면 selectedLayerId도 업데이트
      if (this.selectedLayerId === layerId) {
        const remaining = Array.from(this.selectedLayerIds);
        this.selectedLayerId = remaining.length > 0 ? remaining[remaining.length - 1] : null;
      }
    } else {
      this.selectedLayerIds.add(layerId);
      this.selectedLayerId = layerId;
    }

    eventBus.emit(Events.LAYER_SELECTED, {
      layerId: this.selectedLayerId,
      selectedIds: Array.from(this.selectedLayerIds)
    });
  }

  /**
   * 범위 선택 (Shift+클릭)
   */
  selectRange(startId, endId) {
    const startIndex = this.layerOrder.indexOf(startId);
    const endIndex = this.layerOrder.indexOf(endId);

    if (startIndex === -1 || endIndex === -1) return;

    const minIndex = Math.min(startIndex, endIndex);
    const maxIndex = Math.max(startIndex, endIndex);

    // 범위 내 모든 레이어 선택
    this.selectedLayerIds.clear();
    for (let i = minIndex; i <= maxIndex; i++) {
      this.selectedLayerIds.add(this.layerOrder[i]);
    }

    // 기본 선택 레이어는 클릭한 레이어로 설정
    this.selectedLayerId = endId;

    eventBus.emit(Events.LAYER_SELECTED, {
      layerId: this.selectedLayerId,
      selectedIds: Array.from(this.selectedLayerIds)
    });
  }

  /**
   * 선택에 레이어 추가
   */
  addToSelection(layerId) {
    if (!this.layers.has(layerId)) return;
    this.selectedLayerIds.add(layerId);
    this.selectedLayerId = layerId;

    eventBus.emit(Events.LAYER_SELECTED, {
      layerId: this.selectedLayerId,
      selectedIds: Array.from(this.selectedLayerIds)
    });
  }

  /**
   * 선택에서 레이어 제거
   */
  removeFromSelection(layerId) {
    this.selectedLayerIds.delete(layerId);
    if (this.selectedLayerId === layerId) {
      const remaining = Array.from(this.selectedLayerIds);
      this.selectedLayerId = remaining.length > 0 ? remaining[remaining.length - 1] : null;
    }

    eventBus.emit(Events.LAYER_SELECTED, {
      layerId: this.selectedLayerId,
      selectedIds: Array.from(this.selectedLayerIds)
    });
  }

  /**
   * 모든 선택 해제
   */
  clearSelection() {
    this.selectedLayerIds.clear();
    this.selectedLayerId = null;

    eventBus.emit(Events.LAYER_SELECTED, {
      layerId: null,
      selectedIds: []
    });
  }

  /**
   * 선택 집합을 한 번에 설정 (드래그 박스 선택용)
   * @param {string[]} layerIds - 선택할 레이어 ID 목록
   */
  setSelection(layerIds) {
    this.selectedLayerIds.clear();
    layerIds.forEach(id => {
      if (this.layers.has(id)) this.selectedLayerIds.add(id);
    });
    const arr = Array.from(this.selectedLayerIds);
    this.selectedLayerId = arr.length > 0 ? arr[arr.length - 1] : null;

    eventBus.emit(Events.LAYER_SELECTED, {
      layerId: this.selectedLayerId,
      selectedIds: arr
    });
  }

  /**
   * 선택된 레이어 ID 목록 반환
   */
  getSelectedLayerIds() {
    return Array.from(this.selectedLayerIds);
  }

  /**
   * 선택된 레이어 정보 목록 반환
   */
  getSelectedLayers() {
    const self = this;
    return Array.from(this.selectedLayerIds).map(function(id) {
      return self.layers.get(id);
    }).filter(Boolean);
  }

  /**
   * 레이어가 선택되었는지 확인
   */
  isLayerSelected(layerId) {
    return this.selectedLayerIds.has(layerId);
  }

  getLayer(layerId) {
    return this.layers.get(layerId);
  }

  getAllLayers() {
    const self = this;
    return this.layerOrder.map(function(id) { return self.layers.get(id); }).filter(Boolean);
  }

  reorderLayers(newOrder) {
    const self = this;
    this.layerOrder = newOrder.slice();

    this.layerOrder.forEach(function(layerId, index) {
      const layerInfo = self.layers.get(layerId);
      if (layerInfo) {
        layerInfo.olLayer.setZIndex(index + 1);
      }
    });

    eventBus.emit(Events.LAYER_ORDER_CHANGED, { order: this.layerOrder });
  }

  /** '이름 (사본)', '이름 (사본 2)' … 기존 이름과 겹치지 않는 복사본 이름. */
  duplicateName(name) {
    const names = new Set(this.getAllLayers().map((l) => l.name));
    let candidate = `${name} (사본)`;
    for (let n = 2; names.has(candidate); n++) candidate = `${name} (사본 ${n})`;
    return candidate;
  }

  /**
   * 레이어 복사 — 피처(지오메트리+속성)와 스타일·주제도 설정을 그대로 가진 새 레이어.
   * 벡터 기반 전용: 래스터·도형표현도(HTML 오버레이라 피처가 없음)는 불가 → null.
   * @returns {string|null} 새 레이어 id
   */
  duplicateLayer(layerId) {
    const info = this.layers.get(layerId);
    if (!info || info.type === 'raster' || info.type === 'chartmap'
        || !info.source || typeof info.source.getFeatures !== 'function') return null;

    const features = info.source.getFeatures().map((f) => f.clone()); // clone은 id 미복사 → 충돌 없음
    const newId = this.addLayer({
      name: this.duplicateName(info.name),
      type: info.type,
      geometryType: info.geometryType,
      color: info.color,
      features: features,
      visible: true
    });

    const copy = this.layers.get(newId);
    if (copy) {
      const customized = STYLE_FIELDS.some((k) => info[k] !== undefined && info[k] !== copy[k]);
      STYLE_FIELDS.forEach((k) => { if (info[k] !== undefined) copy[k] = info[k]; });
      // 주제도 설정 유지(분류색 스타일·.egis 저장용). 범례는 원본 것을 공유하므로 중복 생성 안 함.
      if (info._choroplethConfig) copy._choroplethConfig = cloneChoroplethConfig(info._choroplethConfig);
      if (info._cartogramConfig) copy._cartogramConfig = { ...info._cartogramConfig };
      // 주제도 복제본은 분류색 스타일을 다시 걸어야 한다. customized에 기대면 안 된다 —
      // 기본값이 우연히 일치하는 순간 조용히 단색으로 떨어진다.
      if (copy._choroplethConfig || copy._cartogramConfig || customized) this.updateLayerStyle(newId);
    }
    return newId;
  }

  renameLayer(layerId, newName) {
    const layerInfo = this.layers.get(layerId);
    if (layerInfo) {
      layerInfo.name = newName;
      // 파생 레이어(단계구분도·도형표현도 등) 범례 제목 동기화용
      eventBus.emit(Events.LAYER_RENAMED, { layerId, name: newName });
      eventBus.emit(Events.LAYER_ADDED, { layer: layerInfo });
    }
  }

  // 래스터(ImageLayer) 레이어 불투명도 설정 — 벡터 스타일과 별개로 olLayer에 직접 적용
  setRasterOpacity(layerId, opacity) {
    const layerInfo = this.layers.get(layerId);
    if (!layerInfo || !layerInfo.olLayer) return;
    layerInfo.opacity = opacity;
    if (typeof layerInfo.olLayer.setOpacity === 'function') {
      layerInfo.olLayer.setOpacity(opacity);
    }
    eventBus.emit(Events.LAYER_STYLE_CHANGED, { layerId });
  }

  setLayerColor(layerId, newColor) {
    const layerInfo = this.layers.get(layerId);
    if (!layerInfo) return;

    // 분류 레이어는 채우기 색을 분류 설정이 소유한다 (단계구분도·카토그램).
    // 카토그램에서 이걸 막지 않으면 분류색 스타일 함수가 단색으로 덮여 파괴된다.
    if (this.isClassified(layerInfo)) return;

    layerInfo.color = newColor;
    layerInfo.strokeColor = newColor;
    layerInfo.fillColor = newColor;
    this.updateLayerStyle(layerId);
  }

  setLayerStrokeColor(layerId, strokeColor) {
    const layerInfo = this.layers.get(layerId);
    if (!layerInfo) return;

    layerInfo.strokeColor = strokeColor;
    this.updateLayerStyle(layerId);
  }

  setLayerFillColor(layerId, fillColor) {
    const layerInfo = this.layers.get(layerId);
    if (!layerInfo) return;

    // 분류 레이어는 채우기 색을 분류 설정이 소유한다 (단계구분도·카토그램).
    if (this.isClassified(layerInfo)) return;

    layerInfo.fillColor = fillColor;
    this.updateLayerStyle(layerId);
  }

  /** 같은 이름이 이미 있으면 뒤에 번호를 붙인다 */
  uniqueName(base) {
    const taken = new Set(this.getAllLayers().map(l => l.name));
    if (!taken.has(base)) return base;
    let n = 2;
    while (taken.has(`${base} ${n}`)) n++;
    return `${base} ${n}`;
  }

  getColorPalette() {
    return SWATCH_PALETTE.map(s => s.color);
  }

  /** 색 이름까지 필요한 곳(툴팁)에서 쓴다 */
  getColorSwatches() {
    return SWATCH_PALETTE;
  }

  getStrokeDashOptions() {
    return Object.keys(STROKE_DASH_OPTIONS);
  }

  setLayerStrokeDash(layerId, strokeDash) {
    const layerInfo = this.layers.get(layerId);
    if (!layerInfo) return;

    layerInfo.strokeDash = strokeDash;
    this.updateLayerStyle(layerId);
  }

  setLayerFillOpacity(layerId, fillOpacity) {
    const layerInfo = this.layers.get(layerId);
    if (!layerInfo) return;

    layerInfo.fillOpacity = fillOpacity;
    this.updateLayerStyle(layerId);
  }

  setLayerStrokeOpacity(layerId, strokeOpacity) {
    const layerInfo = this.layers.get(layerId);
    if (!layerInfo) return;

    layerInfo.strokeOpacity = strokeOpacity;
    this.updateLayerStyle(layerId);
  }

  setLayerStrokeWidth(layerId, strokeWidth) {
    const layerInfo = this.layers.get(layerId);
    if (!layerInfo) return;

    layerInfo.strokeWidth = strokeWidth;
    this.updateLayerStyle(layerId);
  }

  /**
   * @param {string} layerId
   * @param {{silent?: boolean}} [options] silent: 다시 그리기만 하고 LAYER_STYLE_CHANGED 를 내지 않는다
   *   (시계열 재생 틱 — 틱마다 자동 저장·레이어 패널 다시 그리기를 깨우지 않게. 멈출 때 한 번 낸다)
   */
  updateLayerStyle(layerId, { silent = false } = {}) {
    const layerInfo = this.layers.get(layerId);
    if (!layerInfo) return;

    // 도형표현도: 피처에 구운 차트 아이콘(ChartMapTool.chartIconStyle)이 스타일의 전부
    // — 색/두께 재계산으로 덮어쓰면 차트가 사라진다
    if (layerInfo.type === 'chartmap') return;

    // 흐름 레이어: 캔버스 렌더러라 벡터 스타일이 없다 (스타일은 FlowPanel → flowTool.updateStyle)
    if (layerInfo.type === 'flow') return;

    // 단계구분도: 분류별 색상 유지, 투명도/테두리만 반영
    if (layerInfo.type === 'choropleth' && layerInfo._choroplethConfig) {
      const cfg = layerInfo._choroplethConfig;
      const fillOpacity = layerInfo.fillOpacity !== undefined ? layerInfo.fillOpacity : 0.7;
      const strokeWidth = strokeWidthOf(layerInfo, 1);
      const lineDash = this.getLineDash(layerInfo.strokeDash || "solid");
      // undefined(기존 레이어·기존 저장본)를 기본 ON으로 흡수한다
      const syncStroke = layerInfo.strokeSyncToFill !== false;
      const strokeColor = layerInfo.strokeColor || '#666';
      const styleFn = function(feature) {
        const val = parseFloat(feature.get(cfg.attribute));
        if (isNaN(val)) {
          return new Style({
            fill: new Fill({ color: 'rgba(128,128,128,' + fillOpacity + ')' }),
            stroke: makeStroke({
              color: syncStroke ? '#666' : strokeColor,
              width: strokeWidth,
              lineDash: lineDash
            })
          });
        }
        const colorIdx = cfg.tool.getColorIndex(val, cfg.breaks);
        const color = cfg.colors[colorIdx] || cfg.colors[0];
        return new Style({
          fill: new Fill({ color: cfg.tool.classFillColor(cfg, colorIdx, fillOpacity) }),
          stroke: makeStroke({
            color: syncStroke ? cfg.tool.darkenColor(color) : strokeColor,
            width: strokeWidth,
            lineDash: lineDash
          })
        });
      };
      const olLayer = layerInfo.olLayer;
      if (olLayer && olLayer._hasLabel && olLayer._originalStyle) {
        olLayer._originalStyle = styleFn;
        eventBus.emit('label:refresh', { layerId });
      } else if (olLayer) {
        olLayer.setStyle(styleFn);
      }
      if (!silent) eventBus.emit(Events.LAYER_STYLE_CHANGED, { layerId });
      return;
    }

    // 카토그램: 분류색은 설정이 소유. 사용자는 투명도·테두리만 바꾼다.
    // type이 'cartogram'이 아니라 'vector'라(CartogramTool.js:205) 위 분기에 안 걸린다.
    // 설정 존재로 식별한다(_contourConfig와 같은 방식).
    // tool 참조는 CartogramTool.applyCartogramStyle이 심는다 — 순환 import 회피.
    if (layerInfo._cartogramConfig && layerInfo._cartogramConfig.tool) {
      const cfg = layerInfo._cartogramConfig;
      const styleFn = cfg.tool.cartogramStyle(cfg, {
        fillOpacity: layerInfo.fillOpacity !== undefined ? layerInfo.fillOpacity : 0.85,
        strokeWidth: strokeWidthOf(layerInfo, 1),
        strokeColor: layerInfo.strokeColor || '#333',
        syncStroke: layerInfo.strokeSyncToFill !== false,
        lineDash: this.getLineDash(layerInfo.strokeDash || "solid")
      });
      // 라벨이 걸린 레이어는 setStyle이 라벨 래퍼를 날린다. 단계구분도 분기와 같은 규약으로
      // _originalStyle을 갱신하고 라벨을 다시 그리게 한다 (LabelTool.js:201, 267).
      const olLayer = layerInfo.olLayer;
      if (olLayer && olLayer._hasLabel && olLayer._originalStyle) {
        olLayer._originalStyle = styleFn;
        eventBus.emit('label:refresh', { layerId });
      } else if (olLayer) {
        olLayer.setStyle(styleFn);
      }
      if (!silent) eventBus.emit(Events.LAYER_STYLE_CHANGED, { layerId });
      return;
    }

    // 등고선: 주곡선/계곡선 두께 비율 유지 (색상은 공통, 두께만 차등)
    if (layerInfo._contourConfig) {
      const cfg = layerInfo._contourConfig;
      const strokeColor = layerInfo.strokeColor || layerInfo.color;
      const strokeOpacity = layerInfo.strokeOpacity !== undefined ? layerInfo.strokeOpacity : 1.0;
      const minorWidth = strokeWidthOf(layerInfo, 0.8);
      const rgba = this.hexToRgba(strokeColor, strokeOpacity);
      const lineDash = this.getLineDash(layerInfo.strokeDash || "solid");

      const styleFn = function(feature) {
        const elev = feature.get('elevation');
        const isMajor = elev % (cfg.interval * 5) === 0;
        return new Style({
          stroke: makeStroke({
            color: rgba,
            width: isMajor ? minorWidth * cfg.majorRatio : minorWidth,
            lineDash: lineDash
          })
        });
      };

      const olLayer = layerInfo.olLayer;
      if (olLayer && olLayer._hasLabel && olLayer._originalStyle) {
        olLayer._originalStyle = styleFn;
        eventBus.emit('label:refresh', { layerId });
      } else if (olLayer) {
        olLayer.setStyle(styleFn);
      }
      if (!silent) eventBus.emit(Events.LAYER_STYLE_CHANGED, { layerId });
      return;
    }

    const lineDash = this.getLineDash(layerInfo.strokeDash || "solid");
    const fillOpacity = layerInfo.fillOpacity !== undefined ? layerInfo.fillOpacity : 0.3;
    const strokeOpacity = layerInfo.strokeOpacity !== undefined ? layerInfo.strokeOpacity : 1.0;
    const strokeColor = layerInfo.strokeColor || layerInfo.color;
    const fillColor = layerInfo.fillColor || layerInfo.color;

    const rgbaFill = this.hexToRgba(fillColor, fillOpacity);
    const rgbaStroke = this.hexToRgba(strokeColor, strokeOpacity);

    let newStyle;
    const geoType = layerInfo.geometryType;

    if (geoType === "LineString" || geoType === "MultiLineString") {
      newStyle = new Style({
        stroke: makeStroke({
          color: rgbaStroke,
          width: strokeWidthOf(layerInfo, 2),
          lineDash: lineDash
        })
      });
    } else if (geoType === "Point" || geoType === "MultiPoint") {
      newStyle = new Style({
        image: new CircleStyle({
          radius: layerInfo.pointRadius || 6,
          fill: new Fill({ color: rgbaFill }),
          stroke: makeStroke({ color: rgbaStroke, width: strokeWidthOf(layerInfo, 2) })
        })
      });
    } else {
      newStyle = new Style({
        fill: new Fill({ color: rgbaFill }),
        stroke: makeStroke({
          color: rgbaStroke,
          width: strokeWidthOf(layerInfo, 2),
          lineDash: lineDash
        })
      });
    }

    // 라벨이 적용된 레이어의 경우 _originalStyle을 업데이트하고 라벨 스타일 유지
    const layer = layerInfo.olLayer;
    if (!layer) {
      console.warn('레이어 객체가 없습니다:', layerId);
      return;
    }

    if (layer._hasLabel && layer._originalStyle) {
      layer._originalStyle = newStyle;
      eventBus.emit('label:refresh', { layerId: layerId });
    } else {
      layer.setStyle(newStyle);
    }

    // 레이어 변경 알림 (렌더링 강제)
    if (typeof layer.changed === 'function') {
      layer.changed();
    }

    // 레이어 소스 변경 알림
    if (layerInfo.source && typeof layerInfo.source.changed === 'function') {
      layerInfo.source.changed();
    }

    if (!silent) eventBus.emit(Events.LAYER_STYLE_CHANGED, { layerId: layerId });
    eventBus.emit(Events.LAYER_ADDED, { layer: layerInfo });
  }

  zoomToLayer(layerId) {
    const layerInfo = this.layers.get(layerId);
    if (!layerInfo) return;

    let extent = null;
    if (layerInfo.type === 'flow' && layerInfo._flowConfig) {
      // 흐름 레이어: 피처가 없으니 위치 좌표로 범위를 만든다
      extent = createEmpty();
      for (const loc of layerInfo._flowConfig.dataset.locations) extendCoordinate(extent, fromLonLat([loc.lon, loc.lat]));
    } else if (layerInfo.source && typeof layerInfo.source.getExtent === 'function') {
      extent = layerInfo.source.getExtent();
    }
    if (extent && extent[0] !== Infinity) {
      mapManager.fitExtent(extent, { padding: [50, 50, 50, 50] });
    }
  }

  getSelectedLayerId() {
    return this.selectedLayerId;
  }

  getLayerCount() {
    return this.layers.size;
  }

  clear() {
    const self = this;
    const layerIds = Array.from(this.layers.keys());
    layerIds.forEach(function(id) { self.removeLayer(id); });
  }
}

export const layerManager = new LayerManager();
